// General staff entry. Behavior:
//   • signed out                   → render the inline staff sign-in form
//   • signed in, 0 stations        → redirect to /access-denied
//   • signed in, 1 station         → redirect to /<slug>/admin
//   • signed in, >1 stations       → render the station picker
//
// Super_admins typically land here with the full station catalog; the picker
// renders a search box once the list exceeds 10 entries.

import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Search, ArrowRight, Loader2, HelpCircle } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import StaffSignInForm from '../../components/self-service/auth/StaffSignInForm';
import { useAdminHelpUnread } from '../../components/self-service/help/useAdminHelpUnread';

const SEARCH_THRESHOLD = 10;

const AdminEntryPage: React.FC = () => {
  const { isAuthenticated, user, myAdminStations } = useAuth();
  const isSuperAdmin = user?.rawRole === 'super_admin';
  const { count: adminHelpUnread } = useAdminHelpUnread(isSuperAdmin);
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = React.useState('');

  // After auth, honor a deep-link target carried in router state by
  // PublisherAdminGate / PublisherOnboardPage. Restrict to admin paths to
  // avoid open-redirect surprises. Falls through to the solo-station
  // auto-pick when no valid `from` is present.
  React.useEffect(() => {
    if (!isAuthenticated || myAdminStations == null) return;
    const from = (location.state as { from?: string } | null)?.from;
    if (typeof from === 'string' && from.startsWith('/admin/') && from !== '/admin') {
      navigate(from, { replace: true });
      return;
    }
    if (myAdminStations.length === 1 && myAdminStations[0].slug) {
      navigate(`/admin/${myAdminStations[0].slug}`, { replace: true });
    }
  }, [isAuthenticated, myAdminStations, navigate, location.state]);

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900">Publisher admin</h1>
          </div>
          <StaffSignInForm />
        </div>
      </div>
    );
  }

  if (myAdminStations == null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your stations…
      </div>
    );
  }

  if (myAdminStations.length === 0) {
    return <Navigate to="/access-denied" replace />;
  }

  // Solo-redirect runs in the effect above; render a brief placeholder so we
  // don't flash the picker for the one-station case.
  if (myAdminStations.length === 1) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Redirecting…
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const visible = q
    ? myAdminStations.filter((s) =>
        [s.name, s.displayName ?? '', s.slug ?? '']
          .map((v) => v.toLowerCase())
          .some((v) => v.includes(q)),
      )
    : myAdminStations;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => navigate('/admin/help-center')}
            className="w-full flex items-center justify-between px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-left"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Help Center</div>
                <div className="text-xs text-slate-500">Support tickets from all stations</div>
              </div>
            </div>
            {adminHelpUnread > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-xs font-bold text-white flex items-center justify-center">
                {adminHelpUnread > 9 ? '9+' : adminHelpUnread}
              </span>
            )}
          </button>
        )}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-200">
          <h1 className="text-xl font-semibold text-slate-900">Choose a station</h1>
          <p className="text-sm text-slate-600 mt-1">
            You have admin access to {myAdminStations.length} stations. Pick one to
            open its admin tools — you can switch any time from the sidebar.
          </p>
        </div>

        {myAdminStations.length > SEARCH_THRESHOLD && (
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stations"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {visible.length === 0 ? (
            <li className="px-6 py-8 text-center text-sm text-slate-500">
              No stations match "{search}".
            </li>
          ) : (
            visible.map((s) => (
              <li key={s.slug ?? s.name}>
                <button
                  type="button"
                  disabled={!s.slug}
                  onClick={() => s.slug && navigate(`/admin/${s.slug}`)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-left transition-colors"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {s.displayName || s.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {s.slug ? `/admin/${s.slug}` : 'Slug not set'}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
      </div>
    </div>
  );
};

export default AdminEntryPage;
